import { deepEqual } from 'src/app/common/app.helpers';
import { ProcessMapResponseContent } from 'src/app/interfaces/map.interface';
import { CustomMarker } from 'src/app/models/custom-marker.model';
import { MapMarkers } from 'src/app/models/map-markers.model';
import { SearchAggregationResponse } from 'src/app/swagger-api';
import { MapFilters, ProcessResponseStrategyTypes } from '../../map.enum';
import {
  getMapMarkers,
  getTabRelatedMapState,
  hasAggregatedMarkers,
  hasCovidMarkers,
  isInAggregationZoomRange,
} from '../../map.helpers';

export class MarkersProcessor {
  /**
   * @description Processes the markers list depend on incoming content value.
   *     1. If the passed type is COVID processor strategy, calls processCovidMarkers() method.
   *     2. If the current map state zoom is an aggregated zoom, delegates further steps to
   *        processAggregatedMarkers() method.
   *     3. Otherwise calls processNonAggregatedMarkers() method to process a non-aggregated
   *        markers case.
   * @param { ProcessMapResponseContent } content
   * @param { MapMarkers } mapMarkers
   * @returns { MapMarkers }
   */
  public static processMarkers(content: ProcessMapResponseContent, mapMarkers: MapMarkers): MapMarkers {
    const isEmptyBuckets = Boolean(
      content.response &&
        content.response[0] &&
        (content.response as SearchAggregationResponse)[0].buckets &&
        (content.response as SearchAggregationResponse)[0].buckets.length === 0,
    );
    if (content.state.currentState.filter === MapFilters.COVID && content.type === ProcessResponseStrategyTypes.COVID) {
      return this.processCovidMarkers(content, mapMarkers);
    }

    if (isInAggregationZoomRange(content.state.currentState.zoom)) {
      return this.processAggregatedMarkers(content, isEmptyBuckets ? new MapMarkers() : mapMarkers);
    }

    return this.processNonAggregatedMarkers(content, isEmptyBuckets ? new MapMarkers() : mapMarkers);
  }

  /**
   * @description Simple covid markers processor.
   *     1. If the zoom wasn't changed, combines original markers list with incoming one.
   *     2. Otherwise converts an incoming list to expected object view and returns it.
   * @param { ProcessMapResponseContent } content
   * @param { MapMarkers } mapMarkers
   * @returns { MapMarkers }
   */
  private static processCovidMarkers(content: ProcessMapResponseContent, mapMarkers: MapMarkers): MapMarkers {
    if (content.state.currentState.zoom === content.state.previousState.zoom) {
      return new MapMarkers(this.filterMarkersById(mapMarkers.allMarkers.concat(content.markers)), content.markers, 0);
    }

    return getMapMarkers(content.markers, content.totalMatchesCount);
  }

  /**
   * @description Aggregated markers list processor.
   *     1. If there are no aggregated markers or at least one covid marker in the
   *        incoming markers list, returns original markers list.
   *     2. Otherwise calls shared processMapMarkers() helper with zoom equality condition
   *        check as filterMarkers flag.
   * @param { ProcessMapResponseContent } content
   * @param { MapMarkers } mapMarkers
   * @returns { MapMarkers }
   */
  private static processAggregatedMarkers(content: ProcessMapResponseContent, mapMarkers: MapMarkers): MapMarkers {
    if (!hasAggregatedMarkers(content.markers) || hasCovidMarkers(content.markers)) {
      return mapMarkers;
    }

    return this.processMapMarkers(
      content,
      mapMarkers,
      content.state.currentState.zoom === content.state.previousState.zoom,
    );
  }

  /**
   * @description Non-aggregated markers list processor.
   *     1. If the original markers list has at least one an aggregated or covid marker
   *        in it, converts an incoming list to expected object view and returns it.
   *     3. Otherwise calls shared processMapMarkers() helper.
   * @param { ProcessMapResponseContent } content
   * @param { MapMarkers } mapMarkers
   * @returns { MapMarkers }
   */
  private static processNonAggregatedMarkers(content: ProcessMapResponseContent, mapMarkers: MapMarkers): MapMarkers {
    if (hasAggregatedMarkers(mapMarkers.allMarkers) || hasCovidMarkers(mapMarkers.allMarkers)) {
      return getMapMarkers(content.markers, content.totalMatchesCount);
    }

    return this.processMapMarkers(content, mapMarkers);
  }

  /**
   * @description Processes the markers list depend on attach flag value.
   *     1. If the submitted form content was changed, converts an incoming markers list
   *        to expected object view and returns it.
   *     2. If the filterMarkers flag is truthy, calls filterMarkers() method.
   *     4. Otherwise by default converts an incoming markers list to expected object view
   *        and returns it.
   * @param { ProcessMapResponseContent } content
   * @param mapMarkers
   * @param filterMarkers
   * @returns { CustomMarker[] }
   */
  private static processMapMarkers(
    content: ProcessMapResponseContent,
    mapMarkers: MapMarkers,
    filterMarkers = true,
  ): MapMarkers {
    if (
      !deepEqual(getTabRelatedMapState(content.state.currentState), getTabRelatedMapState(content.state.previousState))
    ) {
      return getMapMarkers(content.markers, content.totalMatchesCount);
    }

    if (filterMarkers) {
      return this.filterMarkers(
        new MapMarkers(content.markers, content.markers, content.totalMatchesCount),
        mapMarkers,
      );
    }

    return getMapMarkers(content.markers, content.totalMatchesCount);
  }

  /**
   * @description Merges original and incoming marker lists. Reduces the duplicated
   *              markers by id in data property. This custom implementation was used
   *              because of the need to keep list items memory link without changes.
   * @param { CustomMarker[] } allMarkers
   * @param { CustomMarker[] } areaMarkers
   */
  private static mergeMarkerLists(allMarkers: CustomMarker[], areaMarkers: CustomMarker[]): MapMarkers {
    const mergedMarkers = new Map([...allMarkers, ...areaMarkers].map((marker: CustomMarker) => [marker.id, marker]));

    return new MapMarkers(Array.from(mergedMarkers.values()), areaMarkers);
  }

  /**
   * @description Returns the processed map markers object without duplicates.
   * @param { MapMarkers } newMapMarkers
   * @param { MapMarkers } oldMapMarkers
   * @returns { MapMarkers }
   */
  private static filterMarkers(newMapMarkers: MapMarkers, oldMapMarkers: MapMarkers): MapMarkers {
    const mapMarkers: MapMarkers = this.mergeMarkerLists(oldMapMarkers.allMarkers, newMapMarkers.areaMarkers);
    mapMarkers.totalMatchesCount = newMapMarkers.totalMatchesCount;
    return mapMarkers;
  }

  /**
   * @description Reduces the duplicated markers by id in data property.
   * @param { CustomMarker[] } markers
   * @returns { CustomMarker[] }
   */
  private static filterMarkersById(markers: CustomMarker[]): CustomMarker[] {
    return markers.filter(
      (marker: CustomMarker, index: number, self: CustomMarker[]) =>
        index === self.findIndex((anotherMarker: CustomMarker) => anotherMarker.id === marker.id),
    );
  }
}
